<template>
  <div class="hero-index" :class="{ 'is-loading': !loaded }">

    <div class="hero-index__head">
      <span class="label-micro hero-index__title">
        <span class="hero-index__pulse" aria-hidden="true"></span>
        Fusca Strong Wool Indicator
      </span>
      <span v-if="index" class="label-micro hero-index__sale">{{ index.sale_numbers }}</span>
    </div>

    <div class="hero-index__figure">
      <span class="figure-primary">
        <span class="figure-primary__unit">$</span>{{ loaded ? displayPrice.toFixed(2) : '0.00' }}
      </span>
      <span class="hero-index__unit label-micro">per kg clean</span>
    </div>

    <div v-if="index" class="hero-index__delta">
      <span :class="isUp ? 'delta-positive' : 'delta-negative'" class="tnum font-medium">
        {{ isUp ? '+' : '' }}{{ index.difference.percentage }}%
      </span>
      <span class="hero-index__delta-period label-micro">
        over {{ index.difference.days }} days
      </span>
    </div>
    <div v-else class="hero-index__delta hero-index__delta--placeholder">&nbsp;</div>

    <div class="hero-index__cells">
      <div class="hero-index__cell">
        <span class="label-micro">Last sale</span>
        <span class="value tnum">{{ loaded ? index.date : '&mdash;' }}</span>
      </div>
      <div class="hero-index__cell">
        <span class="label-micro">Clearance</span>
        <span class="value tnum">{{ loaded ? index.bales.percentage_sold + '%' : '&mdash;' }}</span>
      </div>
    </div>

    <a v-if="chartUrl" :href="chartUrl" class="hero-index__link">
      See the full index
    </a>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';

export default {
  props: {
    chartUrl: {
      type: String,
      default: '',
    },
    /* Server-rendered payload. Starting from it means the figure never flashes
     * back to zero when Vue mounts over the markup Twig already produced. */
    initial: {
      type: Object,
      default: null,
    },
  },

  setup(props) {
    const index = ref(props.initial || null);
    const loaded = ref(!!props.initial);
    const displayPrice = ref(props.initial ? props.initial.price / 100 : 0);

    const isUp = computed(() => {
      return index.value ? index.value.difference.percentage >= 0 : true;
    });

    /* The API reports the indicator in cents per kg clean (the app charts it
     * as c/kg). Growers talk in dollars per kg, so it is converted here and
     * the unit is stated explicitly rather than left ambiguous. */
    const toDollars = (cents) => cents / 100;

    const countUp = (target) => {
      const duration = 900;
      const start = performance.now();

      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        displayPrice.value = target * eased;

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          displayPrice.value = target;
        }
      };

      requestAnimationFrame(step);
    };

    onMounted(async () => {
      /* Only animate when there was nothing server-rendered to begin with;
       * counting up from a number the reader can already see is just jitter. */
      const hadServerValue = !!props.initial;

      try {
        const response = await axios.get('/strong-wool-index/current');
        index.value = response.data;
        loaded.value = true;

        const target = toDollars(index.value.price);
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (hadServerValue || reduceMotion) {
          displayPrice.value = target;
        } else {
          countUp(target);
        }
      } catch (e) {
        /* Keep whatever the server gave us rather than blanking the panel. */
        loaded.value = !!props.initial;
      }
    });

    return { index, loaded, displayPrice, isUp };
  },
};
</script>
